import React from 'react';
import StepHelpButton from '../StepHelpButton';
import { Meeting } from '../../services/meetingService';
import Workshop2026FaqSection from './Workshop2026FaqSection';
import WorkshopShareInviteBlock from './WorkshopShareInviteBlock';
import { useLocale } from '../../context/LocaleContext';
import styles from '../../styles/workshop2026Page.module.css';

interface Workshop2026AboutTabProps {
  isSignedIn: boolean;
  registrationState?: 'none' | 'interest' | 'confirmed';
  firstSessionLabel?: string;
  meeting?: Meeting | null;
  onGoToWorkshopTab?: () => void;
  onLoginForQuestion: () => void;
  userEmail?: string;
  userDisplayName?: string;
}

const Workshop2026AboutTab: React.FC<Workshop2026AboutTabProps> = ({
  isSignedIn,
  registrationState = 'none',
  firstSessionLabel,
  meeting = null,
  onGoToWorkshopTab,
  onLoginForQuestion,
  userEmail,
  userDisplayName,
}) => {
  const { t } = useLocale();

  return (
    <div className={styles.learnContent}>
      {registrationState === 'interest' && (
        <p className={styles.interestBanner}>
          {t('workshop.2026.about.interest_banner')}{' '}
          {onGoToWorkshopTab && (
            <button type="button" className={styles.textButton} onClick={onGoToWorkshopTab}>
              {t('workshop.2026.about.see_workshop_tab')}
            </button>
          )}
        </p>
      )}
      {registrationState === 'confirmed' && (
        <p className={styles.interestBanner}>
          {t('workshop.2026.about.confirmed_banner')}
          {firstSessionLabel
            ? t('workshop.2026.about.first_session_suffix', { date: firstSessionLabel })
            : ''}
          .
          <br />
          {t('workshop.2026.about.zoom_on_workshop_tab')}{' '}
          {onGoToWorkshopTab ? (
            <button type="button" className={styles.textButton} onClick={onGoToWorkshopTab}>
              {t('workshop.2026.about.workshop_tab')}
            </button>
          ) : (
            t('workshop.2026.about.workshop_tab')
          )}{' '}
          -&gt;
        </p>
      )}

      <section className={styles.learnSection}>
        <h2 className={styles.learnHeading}>{t('workshop.2026.about.what_heading')}</h2>
        <p>{t('workshop.2026.about.what_p1')}</p>
        <p>{t('workshop.2026.about.what_p2')}</p>
      </section>

      <section className={styles.learnSection}>
        <h2 className={styles.learnHeading}>{t('workshop.2026.about.for_me_heading')}</h2>
        <ul className={styles.bulletList}>
          <li>{t('workshop.2026.about.for_me_li1')}</li>
          <li>{t('workshop.2026.about.for_me_li2')}</li>
          <li>{t('workshop.2026.about.for_me_li3')}</li>
          <li>{t('workshop.2026.about.for_me_li4')}</li>
        </ul>
        <p>{t('workshop.2026.about.for_me_p1')}</p>
        <p>{t('workshop.2026.about.for_me_p2')}</p>
        <p className={styles.helperText}>{t('workshop.2026.about.for_me_sponsor')}</p>
      </section>

      <section className={styles.learnSection}>
        <h2 className={styles.learnHeading}>{t('workshop.2026.about.week_heading')}</h2>
        <ul className={styles.bulletList}>
          <li>{t('workshop.2026.about.week_li1')}</li>
          <li>{t('workshop.2026.about.week_li2')}</li>
          <li>{t('workshop.2026.about.week_li3')}</li>
        </ul>
      </section>

      <section className={styles.learnSection}>
        <h2 className={styles.learnHeading}>{t('workshop.2026.about.reg_heading')}</h2>
        <ol className={styles.numberedList}>
          <li>{t('workshop.2026.about.reg_li1')}</li>
          <li>{t('workshop.2026.about.reg_li2')}</li>
          <li>{t('workshop.2026.about.reg_li3')}</li>
          <li>{t('workshop.2026.about.reg_li4')}</li>
        </ol>
        {!isSignedIn && (
          <p className={styles.helperText}>{t('workshop.2026.about.reg_signin_hint')}</p>
        )}
      </section>

      <section className={styles.learnSection}>
        <h2 className={styles.learnHeading}>{t('workshop.2026.about.share_heading')}</h2>
        <WorkshopShareInviteBlock />
      </section>

      <section className={styles.learnSection}>
        <h2 className={styles.learnHeading}>{t('workshop.2026.about.rc_heading')}</h2>
        <p>
          {t('workshop.2026.about.rc_body')}{' '}
          <StepHelpButton
            stepKey="workshop"
            variant="link"
            linkLabel={t('workshop.2026.about.rc_help_link')}
          />
        </p>
      </section>

      <Workshop2026FaqSection
        isSignedIn={isSignedIn}
        onLoginForQuestion={onLoginForQuestion}
        userEmail={userEmail}
        userDisplayName={userDisplayName}
        meeting={meeting}
      />
    </div>
  );
};

export default Workshop2026AboutTab;
